import React, {useMemo} from "react";
import {Field, useField} from "react-final-form";
import {errorHandler} from "../../../containers/RegistrationLoginPage/errorUtils";
import {UserLevels} from "../../../containers/RegistrationLoginPage/types";
import {RadioGroupSection} from "../../Registration/widgets/Styled";
import RadioButton from "../RadioButton";
import {ErrorHelperText} from "../Textfield/widgets/Styled";

type RadioButtonSelectedValue = string | number | null;

interface FieldData {
    visible: boolean;
    editable: boolean;
    fieldName: string;
    loading: boolean;
    values:
        | {
              id: UserLevels | number | string;
              label: UserLevels | string;
          }[]
        | null;
    selectedValue: RadioButtonSelectedValue;
}

interface RadioGroupInterface<T extends FieldData> {
    fieldData: T;
    validate(): string | undefined;
    onChangeCallback?(events: React.ChangeEvent<HTMLInputElement>): void;
    validateFields?: string[];
}

const RadioGroupWrapper = function <T extends FieldData, K extends RadioButtonSelectedValue>({
    fieldData,
    validate,
    onChangeCallback,
    validateFields,
}: RadioGroupInterface<T>): JSX.Element {
    const {values, fieldName, selectedValue, editable} = fieldData;
    const {meta} = useField(fieldName);
    const error = meta?.error && meta?.touched;
    const focused = meta?.active;
    const errorMsg = errorHandler(meta);
    const isSelectedValuePresent = useMemo(() => {
        for (const obj of values ?? []) {
            if (obj.id == selectedValue) {
                return true;
            }
        }
        return false;
    }, [selectedValue, values]);

    return useMemo(
        () => (
            <>
                <RadioGroupSection>
                    {values &&
                        values.map((radioButtonField) => {
                            return (
                                <Field<K>
                                    type={"radio"}
                                    key={radioButtonField.id}
                                    name={fieldName}
                                    validate={validate}
                                    value={radioButtonField.id.toString() as K}
                                    {...(selectedValue && isSelectedValuePresent
                                        ? {
                                              initialValue: selectedValue.toString() as K,
                                          }
                                        : {})}
                                    validateFields={validateFields ?? []}
                                >
                                    {({input, meta}) => {
                                        const onChange = input.onChange;
                                        input.onChange = (change) => {
                                            onChangeCallback && onChangeCallback(change);
                                            onChange(change);
                                        };
                                        return (
                                            <RadioButton
                                                label={radioButtonField.label}
                                                isDisabled={!editable}
                                                id={`${fieldName}_${radioButtonField.id}`}
                                                isChecked={input.checked}
                                                name={fieldName}
                                                rff={input}
                                                value={radioButtonField.id.toString()}
                                                isError={
                                                    meta?.error as string | boolean | undefined
                                                }
                                                isTouched={meta?.touched}
                                                isFocused={meta?.active}
                                            />
                                        );
                                    }}
                                </Field>
                            );
                        })}
                </RadioGroupSection>
                {error && focused && (
                    <ErrorHelperText
                        alignLeft
                        ErrorDivStyle={{
                            mobile: {top: "55px", left: "-5px"},
                            laptop: {top: "55px", left: "-5px"},
                        }}
                    >
                        {errorMsg}
                    </ErrorHelperText>
                )}
            </>
        ),
        [
            editable,
            error,
            errorMsg,
            fieldName,
            focused,
            isSelectedValuePresent,
            onChangeCallback,
            selectedValue,
            validate,
            validateFields,
            values,
        ],
    );
};

export default RadioGroupWrapper;
